// Cleared P&L values: the record of every batch mode B removed, and the way
// back from any of them.
//
// WHY THIS IS A RECORD AND NOT A QUEUE. A restored batch stays on this list,
// marked, rather than disappearing. Elijah's failure case is "someone clears
// the wrong project", and the question an hour later is usually not "what is
// still missing" but "what happened to this line". A screen that empties
// itself as things are fixed cannot answer that, and a batch that vanished the
// moment it was restored would look exactly like one that never existed.
//
// WHY IT IS SAFE TO SHOW. The table's SELECT policy is organisation admin AND
// profiles.can_view_financials (20260915000300), so a person without both sees
// no rows at all rather than an empty list with a button they cannot use. The
// component is gated the same way on the client, which is a convenience and
// not the enforcement: the policy is.
//
// WHY THE SKIPPED COUNT IS PRINTED. Restore inserts with ON CONFLICT DO
// NOTHING, so a month somebody retyped after the clear keeps the newer number
// and comes back as skipped. That number goes on screen. A restore that put
// back four of five months must never read like one that put back all five.
//
// The optional project filter exists so the per project entry point can deep
// link straight here with one project's batches already selected, which is
// recovery route 2 of the three in the spec.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, ChevronDown, ChevronRight, Loader2, RotateCcw, ShieldCheck } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useToast } from "../ui/ToastModal";
import { supabase } from "../../lib/supabase";
import { formatCurrency } from "../../lib/calculations";
import { pnlChartCardStyle } from "../../features/pnlAnalytics/pnlAnalyticsFormat";
import {
  describeMonthSpan,
  groupArchiveBatches,
  type ArchiveBatch,
  type ArchiveRow,
} from "../../features/pnlAnalytics/archiveBatches";
import {
  describeRestoreOutcome,
  type RestoreResult,
} from "../../features/pnlAnalytics/clearResultDecode";
import { useClearProjectCategory } from "../../features/pnlAnalytics/useClearProjectCategory";
import "./PnlProjectView.css";

/** The narrow client slice for a table the generated types do not know about yet. */
interface ArchiveReadClient {
  from(table: "claims_pnl_value_archive"): {
    select(columns: string): {
      order(
        column: string,
        options: { ascending: boolean },
      ): PromiseLike<{ data: ArchiveRow[] | null; error: { message: string } | null }>;
    };
  };
}

const ARCHIVE_COLUMNS =
  "clear_batch_id, project_id, category, month_date, amount, source, cleared_at, cleared_by, reason, restored_at";

/**
 * The read itself, holding no state, so the expand effect and the post restore
 * refresh share one path and neither sets state inside an effect body.
 *
 * Not scoped by organisation in the query: the SELECT policy already limits
 * this to an organisation admin with financial access, in their own
 * organisation, and adding a client side filter here would make the screen
 * look correct while depending on a value the client chose.
 */
/**
 * True when the read failed because the archive table is not there at all.
 *
 * That is not an error state for the person looking at the screen, it is the
 * state of a database where 20260915000300 has not been applied yet, and
 * putting a red line about a missing table in front of them says something is
 * wrong when nothing is. Every OTHER failure IS shown, loudly, because a
 * screen that answers "which money is missing" must never answer it from a
 * read it could not perform.
 */
function tableIsAbsent(message: string): boolean {
  const text = message.toLowerCase();
  return text.includes("could not find the table") || text.includes("does not exist");
}

async function fetchArchiveRows(): Promise<{ rows: ArchiveRow[]; error: string | null }> {
  const client = supabase as unknown as ArchiveReadClient;
  const { data, error } = await client
    .from("claims_pnl_value_archive")
    .select(ARCHIVE_COLUMNS)
    .order("cleared_at", { ascending: false });
  if (error) return { rows: [], error: error.message };
  return { rows: data ?? [], error: null };
}

export interface PnlArchivedValuesProps {
  /** Project id to display names for, so a batch reads as a place rather than a uuid. */
  projectNames: Map<string, string>;
  /** When set, only this project's batches are listed, and the heading says so. */
  filterProjectId?: string | null;
  /** Start open. Used by the deep link from a project, where the user asked for this. */
  defaultExpanded?: boolean;
  /** Called after a successful restore, so a caller showing the same data can refetch. */
  onRestored?: (result: RestoreResult) => void;
}

export function PnlArchivedValues({
  projectNames,
  filterProjectId = null,
  defaultExpanded = false,
  onRestored,
}: PnlArchivedValuesProps) {
  const { isAdmin, isSuperAdmin, profile } = useAuth();
  const { addToast } = useToast();
  const { restoring, restoreBatch } = useClearProjectCategory();
  const [expanded, setExpanded] = useState(defaultExpanded);
  // Starts true because the count is unknown until the first fetch, and the
  // collapsed summary must not read "none cleared" before anything was asked.
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ArchiveRow[]>([]);
  const [readError, setReadError] = useState<string | null>(null);
  const [busyBatch, setBusyBatch] = useState<string | null>(null);

  // Both halves, because in this organisation one does not imply the other.
  // This mirrors the SELECT policy rather than replacing it: a user who gets
  // past this sees nothing, because the database returns nothing.
  const canSee = (isAdmin || isSuperAdmin) && Boolean(profile?.can_view_financials);
  const notDeployed = readError !== null && tableIsAbsent(readError);

  // Fetched on mount rather than on expand, unlike the unmapped panel beside
  // it. That panel is lazy because its queries are expensive; this one reads a
  // table that holds one row per deleted value and is empty on day one. The
  // reason it matters: the COLLAPSED header carries the count, and it is also
  // the per project "N cleared on this project" line the recovery path relies
  // on, so a count that is really "nobody has asked yet" would read as "none
  // were cleared" to the person looking for the one that was.
  const load = useCallback(async () => {
    const { rows: fetched, error } = await fetchArchiveRows();
    setReadError(error);
    setRows(fetched);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!canSee) return;
    let cancelled = false;
    void (async () => {
      const { rows: fetched, error } = await fetchArchiveRows();
      if (cancelled) return;
      setReadError(error);
      setRows(fetched);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [canSee]);

  const batches = useMemo(() => {
    const grouped = groupArchiveBatches(rows);
    return filterProjectId ? grouped.filter((batch) => batch.projectId === filterProjectId) : grouped;
  }, [rows, filterProjectId]);

  const outstanding = batches.filter((batch) => !batch.restored).length;

  const restore = useCallback(
    async (batch: ArchiveBatch) => {
      if (!batch.batchId) return;
      setBusyBatch(batch.batchId);
      const outcome = await restoreBatch(batch.batchId);
      setBusyBatch(null);
      if (!outcome.ok) {
        addToast({ type: "error", title: "Nothing was restored", message: outcome.message });
        return;
      }
      addToast({
        type: "success",
        title: `${batch.category} restored`,
        message: describeRestoreOutcome(outcome.result),
        duration: 9000,
      });
      await load();
      onRestored?.(outcome.result);
    },
    [restoreBatch, addToast, load, onRestored],
  );

  if (!canSee || notDeployed) return null;

  const filteredName = filterProjectId ? projectNames.get(filterProjectId) : null;

  return (
    <div style={pnlChartCardStyle} className="pnl-chart-card">
      <button type="button" className="ppv-archive-toggle" onClick={() => setExpanded((open) => !open)} aria-expanded={expanded}>
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <Archive size={18} style={{ color: "#991b1b" }} aria-hidden="true" />
        <span className="ppv-archive-title">Cleared P&amp;L values</span>
        {filteredName && <span className="ppv-archive-note">on {filteredName}</span>}
        {/* "could not be read" is never printed as "0 cleared". This header
            doubles as the per project line somebody consults after a mistake,
            and a failed read reported as a zero says the money is fine. Found
            by rendering it against a database where the archive table does not
            exist yet: PostgREST answered 404 and the header read "0 cleared". */}
        <span className="ppv-archive-note" style={readError ? { color: "#991b1b" } : undefined}>
          ({loading
            ? "loading"
            : readError
            ? "could not be read"
            : `${batches.length} cleared, ${outstanding} not restored`})
        </span>
      </button>

      {expanded && (
        <div style={{ marginTop: 14 }}>
          <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 0 12px", lineHeight: 1.6 }}>
            Typed P&amp;L values removed from a project, kept so that clearing the wrong one can be undone. Restoring
            puts every month back except any where a newer number has been typed since, which is left alone and
            reported. A batch that has been restored stays on this list, marked, so this reads as a record of what
            happened rather than a list of what is outstanding.
          </p>

          {readError && (
            <div className="ppv-notice is-error" role="alert">
              <span>{readError}</span>
            </div>
          )}

          {loading ? (
            <div className="ppv-archive-empty">Loading</div>
          ) : readError ? (
            // Deliberately no empty state underneath the error. Printing "no
            // values have been cleared" below a failed read states as fact the
            // very thing the read could not establish.
            <div className="ppv-archive-empty">
              Nothing can be said about cleared values until that read succeeds.
            </div>
          ) : batches.length === 0 ? (
            <div className="ppv-archive-empty">
              {filteredName
                ? `No P&L values have been cleared on ${filteredName}.`
                : "No P&L values have been cleared."}
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {batches.map((batch) => (
                <div
                  key={batch.batchId ?? `${batch.projectId}-${batch.category}-${batch.clearedAt}`}
                  className={batch.restored ? "ppv-batch is-restored" : "ppv-batch is-pending"}
                >
                  <div className="ppv-batch-main">
                    <div className="ppv-batch-head">
                      <span className="ppv-batch-category">{batch.category}</span>
                      {/* Suppressed when the panel is already filtered to one
                          project: repeating the same project name on every row
                          under a heading that names it is noise, and at 390px
                          it pushes the amount onto a third line. */}
                      {!filterProjectId && (
                        <span className="ppv-batch-project">
                          on {projectNames.get(batch.projectId) ?? "a project you cannot see"}
                        </span>
                      )}
                      <span data-seq-private="" className="ppv-batch-total">{formatCurrency(batch.total, true)}</span>
                    </div>
                    <div className="ppv-batch-meta">
                      {batch.rowCount} {batch.rowCount === 1 ? "value" : "values"}, {describeMonthSpan(batch)}. Cleared{" "}
                      {new Date(batch.clearedAt).toLocaleString("en-AU")}
                      {batch.clearedBy ? "" : " by an unidentified session"}
                      {batch.reason === "direct_delete"
                        ? ". Deleted straight from the value table, not through the clear dialog, so there is no batch to restore as a unit."
                        : "."}
                    </div>
                  </div>
                  <div className="ppv-batch-action">
                    {batch.restored ? (
                      <span className="ppv-restored-mark">
                        <ShieldCheck size={13} aria-hidden="true" /> Restored
                      </span>
                    ) : batch.batchId ? (
                      <button
                        type="button"
                        className="ppv-icon-btn"
                        style={{ width: "auto", minWidth: 44, padding: "0 12px", gap: 6, fontSize: 12.5, fontWeight: 600 }}
                        disabled={restoring || busyBatch === batch.batchId}
                        onClick={() => restore(batch)}
                      >
                        {busyBatch === batch.batchId ? (
                          <Loader2 size={13} className="spin" aria-hidden="true" />
                        ) : (
                          <RotateCcw size={13} aria-hidden="true" />
                        )}
                        {busyBatch === batch.batchId ? "Restoring" : "Restore"}
                      </button>
                    ) : (
                      <span className="ppv-batch-meta">No batch id</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
